import React from 'react'
import { cm } from '~/utils/utils'

interface WebkitDirectoryHTMLInputElement extends HTMLInputElement {
  webkitdirectory: boolean
}

type FileWithRelativePath = File & {
  webkitRelativePath?: string
}

type DataTransferItemWithEntry = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntry | null
}

type WindowWithDirectoryPicker = Window & {
  showDirectoryPicker?: () => Promise<FileSystemDirectoryHandle>
}

function createSyntheticFileList(files: File[]): FileList {
  const fileList: Partial<FileList> & { [index: number]: File } = {
    length: files.length,
    item: (index: number) => files[index] ?? null,
    [Symbol.iterator]: function* () {
      yield* files
    }
  }

  files.forEach((file, index) => {
    fileList[index] = file
  })

  return fileList as FileList
}

function createSyntheticChangeEvent(
  files: FileList
): React.ChangeEvent<WebkitDirectoryHTMLInputElement> {
  return {
    target: { files },
    currentTarget: { files },
    preventDefault: () => {},
    stopPropagation: () => {}
  } as unknown as React.ChangeEvent<WebkitDirectoryHTMLInputElement>
}

async function readDroppedFileEntry(
  entry: FileSystemFileEntry,
  relativePath: string
): Promise<File> {
  const file = await new Promise<File>((resolve, reject) => {
    entry.file(resolve, reject)
  })

  const clonedFile = new File([file], file.name, {
    type: file.type,
    lastModified: file.lastModified
  }) as FileWithRelativePath

  Object.defineProperty(clonedFile, 'webkitRelativePath', {
    configurable: true,
    writable: true,
    value: relativePath
  })

  return clonedFile
}

async function readDirectoryEntries(
  directory: FileSystemDirectoryEntry
): Promise<FileSystemEntry[]> {
  const reader = directory.createReader()
  const entries: FileSystemEntry[] = []

  while (true) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject)
    })

    if (batch.length === 0) {
      return entries
    }

    entries.push(...batch)
  }
}

async function collectDroppedDirectoryFiles(
  entry: FileSystemEntry,
  basePath = ''
): Promise<File[]> {
  if (entry.isFile) {
    return [await readDroppedFileEntry(entry as FileSystemFileEntry, `${basePath}${entry.name}`)]
  }

  const directory = entry as FileSystemDirectoryEntry
  const entries = await readDirectoryEntries(directory)
  const nestedFiles = await Promise.all(
    entries.map(child => collectDroppedDirectoryFiles(child, `${basePath}${directory.name}/`))
  )

  return nestedFiles.flat()
}

async function collectDirectoryHandleFiles(
  directoryHandle: FileSystemDirectoryHandle,
  basePath = directoryHandle.name
): Promise<File[]> {
  const handleWithValues = directoryHandle as FileSystemDirectoryHandle & {
    values(): AsyncIterable<FileSystemHandle>
  }

  const files: File[] = []
  for await (const entry of handleWithValues.values()) {
    const entryPath = `${basePath}/${entry.name}`

    if (entry.kind === 'file') {
      const fileHandle = entry as FileSystemFileHandle
      const file = await fileHandle.getFile()
      const clonedFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      }) as FileWithRelativePath

      Object.defineProperty(clonedFile, 'webkitRelativePath', {
        configurable: true,
        writable: true,
        value: entryPath
      })

      files.push(clonedFile)
      continue
    }

    const childDirectory = entry as FileSystemDirectoryHandle
    files.push(...(await collectDirectoryHandleFiles(childDirectory, entryPath)))
  }

  return files
}

export type UploadFileVariant = 'with-toggle' | 'without-toggle'
export type UploadFileStatus = 'default' | 'uploading' | 'uploaded' | 'error' | null

export interface UploadState {
  status: UploadFileStatus
  progress?: number
  error?: string
  file?: { name: string; size: number }
}

export interface UploadFileProps {
  variant: UploadFileVariant
  uploadState: UploadState
  defaultIcon?: React.ReactNode
  uploadedIcon?: React.ReactNode
  mainTitle?: string
  description?: string
  fileName?: string
  onFileSelect?: (
    e: React.ChangeEvent<WebkitDirectoryHTMLInputElement>,
    isSecondary?: boolean
  ) => void
  onDropFile?: (file: File, isSecondary?: boolean) => void
  onRemove?: () => void
  onToggleChange?: (enabled: boolean) => void
  isToggleEnabled?: boolean
  onErrorRemove?: () => void
  isSecondary?: boolean
  inputId?: string
  accept?: string
  webkitdirectory?: boolean
  disabled?: boolean
  className?: string
}

export const truncateFileName = (fileName: string | undefined, maxWidth = 286) => {
  if (!fileName) return ''
  // Create a temporary element to measure text width
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return fileName

  // Set font to match the component's text style
  ctx.font =
    "14px 'Geist', ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

  // If text fits within maxWidth, return as is
  if (ctx.measureText(fileName).width <= maxWidth) {
    return fileName
  }

  // Split filename and extension
  const lastDotIndex = fileName.lastIndexOf('.')
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : ''

  // If name is too short to truncate meaningfully, return original
  if (name.length <= 4) {
    return fileName
  }

  // Get last 2 characters of the name (before extension)
  const lastTwoChars = name.slice(-2)
  const ellipsis = '...'
  const suffix = lastTwoChars + extension

  // Binary search to find the optimal prefix length
  let left = 1
  let right = name.length - 3 // Leave space for last 2 chars + ellipsis
  let bestPrefix = name.substring(0, 1)

  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    const prefix = name.substring(0, mid)
    const testString = prefix + ellipsis + suffix

    if (ctx.measureText(testString).width <= maxWidth) {
      bestPrefix = prefix
      left = mid + 1
    } else {
      right = mid - 1
    }
  }

  return bestPrefix + ellipsis + suffix
}

export const UploadFile: React.FC<UploadFileProps> = ({
  variant,
  uploadState,
  defaultIcon,
  uploadedIcon,
  mainTitle = 'Upload your repository',
  description = 'Click or drag and drop your file with max size of 5 MB',
  fileName,
  onFileSelect,
  onDropFile,
  onRemove,
  onToggleChange,
  isToggleEnabled = false,
  onErrorRemove,
  isSecondary = false,
  inputId = 'fileUpload',
  accept,
  webkitdirectory,
  disabled = false,
  className
}) => {
  const { status, progress, error, file } = uploadState

  // Generate unique ID for clipPath to avoid conflicts when multiple components render
  const clipPathId = React.useMemo(() => `clip_${Math.random().toString(36).substr(2, 9)}`, [])
  const [dragActive, setDragActive] = React.useState(false)
  const supportsDirectoryPicker = React.useMemo(() => {
    if (!webkitdirectory || typeof window === 'undefined') return false
    return typeof (window as WindowWithDirectoryPicker).showDirectoryPicker === 'function'
  }, [webkitdirectory])

  React.useEffect(() => {
    if (status !== 'default' && status !== null && status !== undefined) {
      setDragActive(false)
    }
  }, [status])

  // Toggle component
  const Toggle = ({
    enabled,
    onChange
  }: {
    enabled: boolean
    onChange: (enabled: boolean) => void
  }) => (
    <button
      className={cm(
        'w-[28px] h-4 rounded-[16px] transition-all duration-200 relative flex items-center',
        enabled ? 'bg-[rgba(0,0,0,0.88)]' : 'bg-[#D6D6D6]'
      )}
      onClick={() => onChange(!enabled)}
    >
      <div
        className={cm(
          'w-3 h-3 bg-white rounded-full shadow-sm transition-transform duration-200',
          enabled ? 'transform translate-x-[14px]' : 'transform translate-x-0.5'
        )}
      />
    </button>
  )

  // Loading spinner
  const LoadingSpinner = () => (
    <div className='w-6 h-6 flex items-center justify-center'>
      <svg
        className='animate-spin'
        xmlns='http://www.w3.org/2000/svg'
        width='18'
        height='18'
        viewBox='0 0 18 18'
        fill='none'
      >
        <g clipPath='url(#clip0_156_3544)'>
          <path
            d='M9 18C7.78535 18 6.60586 17.7627 5.49668 17.2934C4.42441 16.8398 3.46289 16.1895 2.63672 15.3633C1.81055 14.5371 1.16016 13.5756 0.706641 12.5033C0.237305 11.3941 0 10.2146 0 9C0 8.6502 0.283008 8.36719 0.632812 8.36719C0.982617 8.36719 1.26562 8.6502 1.26562 9C1.26562 10.0441 1.46953 11.0566 1.87383 12.0111C2.26406 12.9322 2.82129 13.7602 3.53145 14.4703C4.2416 15.1805 5.06953 15.7395 5.99062 16.1279C6.94336 16.5305 7.95586 16.7344 9 16.7344C10.0441 16.7344 11.0566 16.5305 12.0111 16.1262C12.9322 15.7359 13.7602 15.1787 14.4703 14.4686C15.1805 13.7584 15.7395 12.9305 16.1279 12.0094C16.5305 11.0566 16.7344 10.0441 16.7344 9C16.7344 7.95586 16.5305 6.94336 16.1262 5.98887C15.7373 5.06999 15.1744 4.23494 14.4686 3.52969C13.7641 2.8229 12.9288 2.25991 12.0094 1.87207C11.0566 1.46953 10.0441 1.26562 9 1.26562C8.6502 1.26562 8.36719 0.982617 8.36719 0.632812C8.36719 0.283008 8.6502 0 9 0C10.2146 0 11.3941 0.237305 12.5033 0.706641C13.5756 1.16016 14.5371 1.81055 15.3633 2.63672C16.1895 3.46289 16.8381 4.42617 17.2916 5.49668C17.7609 6.60586 17.9982 7.78535 17.9982 9C17.9982 10.2146 17.7609 11.3941 17.2916 12.5033C16.8398 13.5756 16.1895 14.5371 15.3633 15.3633C14.5371 16.1895 13.5738 16.8381 12.5033 17.2916C11.3941 17.7627 10.2146 18 9 18Z'
            fill='black'
            fillOpacity='0.45'
          />
        </g>
        <defs>
          <clipPath id={clipPathId}>
            <rect width='18' height='18' fill='white' />
          </clipPath>
        </defs>
      </svg>
    </div>
  )

  const redDeleteIcon = () => (
    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
      <path
        d='M6.32812 3.23438H6.1875C6.26484 3.23438 6.32812 3.17109 6.32812 3.09375V3.23438H11.6719V3.09375C11.6719 3.17109 11.7352 3.23438 11.8125 3.23438H11.6719V4.5H12.9375V3.09375C12.9375 2.47324 12.433 1.96875 11.8125 1.96875H6.1875C5.56699 1.96875 5.0625 2.47324 5.0625 3.09375V4.5H6.32812V3.23438ZM15.1875 4.5H2.8125C2.50137 4.5 2.25 4.75137 2.25 5.0625V5.625C2.25 5.70234 2.31328 5.76562 2.39062 5.76562H3.45234L3.88652 14.959C3.91465 15.5584 4.41035 16.0312 5.00977 16.0312H12.9902C13.5914 16.0312 14.0854 15.5602 14.1135 14.959L14.5477 5.76562H15.6094C15.6867 5.76562 15.75 5.70234 15.75 5.625V5.0625C15.75 4.75137 15.4986 4.5 15.1875 4.5ZM12.8549 14.7656H5.14512L4.71973 5.76562H13.2803L12.8549 14.7656Z'
        fill='#D94B36'
      />
    </svg>
  )

  // Get text color based on toggle state for with-toggle variant
  const getTextColor = () => {
    if (variant === 'with-toggle' && status === 'uploaded' && !isToggleEnabled) {
      return 'text-[rgba(0,0,0,0.65)]'
    }
    return 'text-[rgba(0,0,0,0.88)]'
  }

  // Render content based on status
  const renderContent = () => {
    // Uploading state
    if (status === 'uploading') {
      return (
        <div className='flex items-center gap-4 h-full w-full'>
          <LoadingSpinner />
          <div className='flex-1'>
            <div className={cm('text-base font-medium leading-[20px]', getTextColor())}>
              Settings
            </div>
            <div className='text-sm text-black/45 leading-[22px] font-medium'>
              Uploading ({progress || 0}%)
            </div>
          </div>
        </div>
      )
    }

    // Uploaded state
    if (status === 'uploaded') {
      const displayFileName = fileName || file?.name || mainTitle
      const truncatedFileName =
        typeof displayFileName === 'string' ? truncateFileName(displayFileName) : displayFileName
      return (
        <div className='group flex items-center justify-between h-full w-full'>
          <div className='flex items-center gap-[16px]'>
            {variant === 'with-toggle' ? (
              <Toggle enabled={isToggleEnabled} onChange={onToggleChange || (() => {})} />
            ) : (
              <div className='w-[24px] h-[24px] flex items-center justify-center shrink-0 text-[rgba(0,0,0,0.88)] select-none'>
                {uploadedIcon}
              </div>
            )}
            <div className='font-geist-mono text-[12px] font-semibold leading-normal text-[rgba(0,0,0,0.88)] max-w-[286px] whitespace-nowrap select-text'>
              {truncatedFileName}
            </div>
          </div>
          {onRemove && (
            <div
              className='w-[24px] h-[24px] flex items-center justify-center shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity text-[rgba(0,0,0,0.45)]'
              onClick={onRemove}
            >
              {/* RemoveIcon will be passed from parent */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  d='M6.32812 3.23438H6.1875C6.26484 3.23438 6.32812 3.17109 6.32812 3.09375V3.23438H11.6719V3.09375C11.6719 3.17109 11.7352 3.23438 11.8125 3.23438H11.6719V4.5H12.9375V3.09375C12.9375 2.47324 12.433 1.96875 11.8125 1.96875H6.1875C5.56699 1.96875 5.0625 2.47324 5.0625 3.09375V4.5H6.32812V3.23438ZM15.1875 4.5H2.8125C2.50137 4.5 2.25 4.75137 2.25 5.0625V5.625C2.25 5.70234 2.31328 5.76562 2.39062 5.76562H3.45234L3.88652 14.959C3.91465 15.5584 4.41035 16.0312 5.00977 16.0312H12.9902C13.5914 16.0312 14.0854 15.5602 14.1135 14.959L14.5477 5.76562H15.6094C15.6867 5.76562 15.75 5.70234 15.75 5.625V5.0625C15.75 4.75137 15.4986 4.5 15.1875 4.5ZM12.8549 14.7656H5.14512L4.71973 5.76562H13.2803L12.8549 14.7656Z'
                  fill='currentColor'
                />
              </svg>
            </div>
          )}
        </div>
      )
    }

    // Error state
    if (status === 'error') {
      const displayFileName = fileName || file?.name || mainTitle
      return (
        <div className='flex items-center justify-between h-full w-full'>
          <div className='flex items-center gap-[16px]'>
            <div className='w-[24px] h-[24px] flex items-center justify-center shrink-0 text-[rgba(0,0,0,0.88)] select-none'>
              {uploadedIcon}
            </div>
            <div className='flex-1'>
              <div className='text-base font-medium leading-[20px] text-[#2E1F36] select-text'>
                {displayFileName}
              </div>
              <div className='text-[#D94B36] text-[10px] font-mono font-semibold leading-4 select-none'>
                {error}
              </div>
            </div>
          </div>
          {onErrorRemove && (
            <div
              className='w-[24px] h-[24px] flex items-center justify-center shrink-0 cursor-pointer text-[#D94B36]'
              onClick={e => {
                e.stopPropagation()
                onErrorRemove()
              }}
            >
              {redDeleteIcon()}
            </div>
          )}
        </div>
      )
    }

    // Default state - horizontal layout matching Figma spec
    return (
      <div className='flex items-center gap-[16px] w-full h-full'>
        <div className='w-[24px] h-[24px] flex items-center justify-center shrink-0 text-[rgba(0,0,0,0.45)] select-none'>
          {defaultIcon}
        </div>
        <div className='flex flex-col gap-[4px] flex-1 justify-center'>
          <div className='text-left font-geist text-[14px] font-medium leading-[22px] text-[#5B5BD5] select-none'>
            {mainTitle}
          </div>
          <div className='text-left font-geist text-[11px] font-normal leading-normal text-[rgba(0,0,0,0.45)] select-none'>
            {description}
          </div>
        </div>
      </div>
    )
  }

  // Get border style based on status
  const getBorderStyle = () => {
    if (status === 'uploaded') {
      return 'border-solid'
    }
    return 'border-dashed'
  }

  // Determine if component should be clickable
  const isClickable = status !== 'uploaded' && status !== 'error'

  // Get container height based on content lines
  const getContainerHeight = () => {
    // Default state: match Figma spec (72px)
    if (status === 'default' || !status) {
      return 'h-[72px]'
    }
    // Uploading/error: Two lines
    if (status === 'uploading' || status === 'error') {
      return 'h-[74px]'
    }
    // One line: uploaded state
    return 'h-[72px]'
  }

  const containerClasses = cm(
    'flex rounded-[16px] border border-[#EAEAEA] bg-[rgba(189,189,189,0.08)] transition-colors w-[360px]',
    getContainerHeight(),
    getBorderStyle(),
    // Consistent padding of 16px for all states
    'p-[16px]',
    isClickable && !disabled && 'cursor-pointer',
    disabled && 'opacity-50 cursor-not-allowed',
    dragActive &&
      !disabled &&
      (status === 'default' || !status) &&
      'border-[#262626] bg-[rgba(189,189,189,0.16)]',
    className
  )

  // For default states, render as label for file input
  if ((status === 'default' || !status) && onFileSelect) {
    const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) return
      setDragActive(true)
    }

    const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) return
      if (!dragActive) {
        setDragActive(true)
      }
    }

    const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) return
      const related = event.relatedTarget as Node | null
      if (related && event.currentTarget.contains(related)) {
        return
      }
      setDragActive(false)
    }

    const handleDrop = (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) return
      setDragActive(false)
      const droppedFiles = event.dataTransfer?.files
      const droppedItems = event.dataTransfer?.items
      if (
        (!droppedFiles || droppedFiles.length === 0) &&
        (!droppedItems || droppedItems.length === 0)
      ) {
        return
      }
      const droppedFile = droppedFiles?.[0]

      if (onDropFile && droppedFile) {
        onDropFile(droppedFile, isSecondary)
        return
      }

      if (onFileSelect) {
        ;(async () => {
          try {
            if (webkitdirectory && droppedItems?.length) {
              for (const item of Array.from(droppedItems)) {
                const entry = (item as DataTransferItemWithEntry).webkitGetAsEntry?.()
                if (entry?.isDirectory) {
                  const files = await collectDroppedDirectoryFiles(entry)
                  if (files.length > 0) {
                    onFileSelect(
                      createSyntheticChangeEvent(createSyntheticFileList(files)),
                      isSecondary
                    )
                  }
                  return
                }
              }
            }

            if (!droppedFiles || droppedFiles.length === 0) {
              return
            }

            const files = webkitdirectory
              ? Array.from(droppedFiles)
              : [droppedFile ?? droppedFiles[0]]

            onFileSelect(
              createSyntheticChangeEvent(createSyntheticFileList(files.filter(Boolean) as File[])),
              isSecondary
            )
          } catch (err) {
            console.error('Error processing dropped file:', err)
          }
        })()
      }
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (!supportsDirectoryPicker || disabled || !onFileSelect) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      ;(async () => {
        try {
          const picker = (window as WindowWithDirectoryPicker).showDirectoryPicker
          if (!picker) {
            return
          }

          const directoryHandle = await picker()
          const files = await collectDirectoryHandleFiles(directoryHandle)
          onFileSelect(createSyntheticChangeEvent(createSyntheticFileList(files)), isSecondary)
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            return
          }
          console.error('Error selecting directory:', error)
        }
      })()
    }

    if (supportsDirectoryPicker) {
      return (
        <button
          type='button'
          className={containerClasses}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          disabled={disabled}
        >
          {renderContent()}
        </button>
      )
    }

    return (
      <>
        <input
          type='file'
          id={inputId}
          className='hidden'
          onChange={e => onFileSelect?.(e, isSecondary)}
          accept={accept}
          disabled={disabled}
          {...(webkitdirectory && { webkitdirectory: 'true', multiple: true })}
        />
        <label
          htmlFor={inputId}
          className={containerClasses}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {renderContent()}
        </label>
      </>
    )
  }

  // For uploaded, uploading and error states, render as div
  return <div className={containerClasses}>{renderContent()}</div>
}
